import { useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import type { Drawing, Question, Profile, Tag, LearningSession } from '../types/database'
import { Sidebar } from './Sidebar'
import { DrawingDetail } from './DrawingDetail'
import { useAuth } from '../contexts/AuthContext'
import { getFileUrl } from '../utils/fileUtils'
import { DrawingCanvas } from './DrawingCanvas'
import html2canvas from 'html2canvas'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

type DrawingWithQuestion = Drawing & {
  duration: number | null
  question?: (Question & {
    question_groups: {
      books: {
        code: string
      } | null
    } | null
  }) | null
  user?: Profile | null
  tags?: Tag[]
}

type StatusFilter = 'default' | 'finished' | 'live' | 'all'

export function DrawingList() {
  const { session, roles } = useAuth()
  const [searchParams] = useSearchParams()
  const initialSessionId = searchParams.get('session') || 'all'

  // Check if user can delete a specific drawing
  const canDeleteDrawing = () => {
    if (!session) return false
    if (roles.includes('admin') || roles.includes('moderator')) return true;
    return false;
  }
  const [drawings, setDrawings] = useState<DrawingWithQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDrawingId, setSelectedDrawingId] = useState<number | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('default')

  // Tag and user filter state
  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [availableUsers, setAvailableUsers] = useState<Profile[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string>('all')
  const [availableSessions, setAvailableSessions] = useState<LearningSession[]>([])
  const [selectedSessionId, setSelectedSessionId] = useState<string>(initialSessionId)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [totalFilteredCount, setTotalFilteredCount] = useState(0)

  // Selection & Batch Download State
  const [selectedDrawingIds, setSelectedDrawingIds] = useState<Set<number>>(new Set())
  const [isProcessingBatch, setIsProcessingBatch] = useState(false)
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 })
  const [batchCurrentDrawing, setBatchCurrentDrawing] = useState<DrawingWithQuestion | null>(null)
  const [batchCurrentPdfUrl, setBatchCurrentPdfUrl] = useState<string | null>(null)
  const canvasRef = useRef<HTMLDivElement>(null)

  // Ref to store callbacks to resolve promises for batch processing
  const batchResolveRef = useRef<((value: unknown) => void) | null>(null)

  const fetchDrawings = async () => {
    try {
      setLoading(true)

      // 1. Get filtered IDs and total count from RPC
      const { data: searchResults, error: searchError } = await supabase
        .rpc('search_drawings', {
          p_search_query: searchQuery,
          p_status_filter: statusFilter,
          p_user_id: selectedUserId,
          p_session_id: selectedSessionId,
          p_tag_ids: selectedTagIds,
          p_page: currentPage,
          p_page_size: itemsPerPage
        })

      if (searchError) throw searchError

      if (!searchResults || searchResults.length === 0) {
        setDrawings([])
        setTotalFilteredCount(0)
        return
      }

      // Extract IDs and total count
      // All rows have same total_count, so take from first row
      const totalCount = searchResults[0].total_count
      const drawingIds = searchResults.map((r: any) => r.id)

      setTotalFilteredCount(Number(totalCount))

      // 2. Fetch full details for these IDs
      const { data, error } = await supabase
        .from('drawings')
        .select(`
          *,
          question:questions(
            *,
            question_groups(
              books(
                code
              )
            )
          ),
          user:profiles(*)
        `)
        .in('id', drawingIds)
        .order('created_at', { ascending: false })


      if (error) throw error

      // Fetch tags for each drawing
      let drawingsWithTags: DrawingWithQuestion[] = (data || []) as any

      if (data && data.length > 0) {
        const { data: drawingTagsData } = await supabase
          .from('drawing_tags')
          .select('drawing_id, tags(*)')
          .in('drawing_id', drawingIds)

        // Map tags to drawings
        drawingsWithTags = data.map((drawing) => {
          const drawingTags = (drawingTagsData || [])
            .filter((dt: any) => dt.drawing_id === drawing.id)
            .map((dt: any) => dt.tags)
            .filter(Boolean) as Tag[]
          return { ...drawing, tags: drawingTags }
        }) as any
      }

      // Sort again by created_at desc to match default, because .in() might not preserve order
      drawingsWithTags.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

      setDrawings(drawingsWithTags)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch drawings')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        // Fetch available tags
        const { data: tagsData, error: tagsError } = await supabase
          .from('tags')
          .select('*')
          .order('tag_type', { ascending: true })
          .order('name', { ascending: true })

        if (tagsError) throw tagsError
        setAvailableTags(tagsData || [])

        // Fetch available users who have drawings
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('*')
          .order('last_name', { ascending: true })
          .order('first_name', { ascending: true })

        if (usersError) throw usersError
        setAvailableUsers(usersData || [])

        // Fetch available sessions (published only)
        const { data: sessionsData, error: sessionsError } = await supabase
          .from('learning_sessions')
          .select('*')
          .eq('status', 1)  // published sessions only
          .order('name', { ascending: true })

        if (sessionsError) throw sessionsError
        setAvailableSessions(sessionsData || [])
      } catch (err) {
        console.error('Failed to fetch filters:', err)
      }
    }

    fetchFilters()
  }, [])

  useEffect(() => {
    fetchDrawings()
  }, [statusFilter, selectedUserId, selectedSessionId, searchQuery, selectedTagIds, currentPage, itemsPerPage])

  // Reset to first page when search query or status filter changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, selectedUserId, selectedSessionId, selectedTagIds])

  // Automatically trigger duration update if missing
  useEffect(() => {
    if (drawings.length === 0) return

    const drawingsMissingDuration = drawings.filter(d => d.duration === null && d.drawing_data)
    if (drawingsMissingDuration.length === 0) return

    const updateDurations = async () => {
      // Process one by one to avoid rate limits or overwhelming the server
      for (const drawing of drawingsMissingDuration) {
        try {
          // Trigger the edge function
          await supabase.functions.invoke('update-duration', {
            body: { drawingId: drawing.id }
          })

          // Optimistically update or re-fetch (re-fetching might be heavy, so we rely on finding it again on refresh)
          // Or we can just log success
          console.log(`Triggered duration update for drawing ${drawing.id}`)
        } catch (e) {
          console.error(`Failed to update duration for drawing ${drawing.id}`, e)
        }
      }
    }

    // We don't await this in the useEffect hook
    updateDurations()
  }, [drawings])


  const handleDelete = async (id: number) => {
    if (!confirm('정말 삭제하시겠습니까?')) return

    try {
      const { error } = await supabase
        .from('drawings')
        .delete()
        .eq('id', id)

      if (error) throw error
      await fetchDrawings()
      // Remove from selection if it was selected
      if (selectedDrawingIds.has(id)) {
        const newSet = new Set(selectedDrawingIds)
        newSet.delete(id)
        setSelectedDrawingIds(newSet)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete drawing')
    }
  }

  const handleBackToList = () => {
    setSelectedDrawingId(null)
    fetchDrawings()
  }

  // Selection Handlers
  const toggleSelection = (id: number) => {
    const newSet = new Set(selectedDrawingIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedDrawingIds(newSet)
  }

  const toggleAllSelection = () => {
    if (selectedDrawingIds.size === paginatedDrawings.length && paginatedDrawings.length > 0) {
      // If all currently visible are selected, deselect them
      const newSet = new Set(selectedDrawingIds)
      paginatedDrawings.forEach(d => newSet.delete(d.id))
      setSelectedDrawingIds(newSet)
    } else {
      // Select all currently visible
      const newSet = new Set(selectedDrawingIds)
      paginatedDrawings.forEach(d => newSet.add(d.id))
      setSelectedDrawingIds(newSet)
    }
  }

  const handleBatchDownload = async () => {
    if (selectedDrawingIds.size === 0) return

    setIsProcessingBatch(true)
    setBatchProgress({ current: 0, total: selectedDrawingIds.size })

    const zip = new JSZip()
    const selectedDrawings = drawings.filter(d => selectedDrawingIds.has(d.id))

    try {
      for (let i = 0; i < selectedDrawings.length; i++) {
        const drawing = selectedDrawings[i]
        setBatchProgress({ current: i + 1, total: selectedDrawings.length })
        setBatchCurrentDrawing(drawing)

        // Fetch PDF URL if needed
        let pdfUrl = null
        if (drawing.question?.file) {
          // @ts-ignore
          const bookCode = drawing.question.question_groups?.books?.code
          pdfUrl = await getFileUrl(drawing.question.file!, bookCode)
        }
        setBatchCurrentPdfUrl(pdfUrl)

        // Wait for canvas to be ready and captured
        const blob = await new Promise<Blob | null>((resolve) => {
          batchResolveRef.current = async () => {
            if (canvasRef.current) {
              // The ID for the first page canvas in DrawingCanvas is usually `drawing-canvas-0`
              // However, we are rendering DrawingCanvas inside our hidden div.
              // We need to capture the specific element.
              // DrawingCanvas renders with IDs. We need to be careful about ID conflicts if multiple are rendered,
              // but here we render one at a time.
              const target = document.getElementById('drawing-canvas-0')
              if (target) {
                try {
                  const canvas = await html2canvas(target, {
                    useCORS: true,
                    allowTaint: true,
                    scale: 2,
                    backgroundColor: null
                  } as any)
                  canvas.toBlob((b) => resolve(b), 'image/png')
                } catch (e) {
                  console.error(e)
                  resolve(null)
                }
              } else {
                resolve(null)
              }
            } else {
              resolve(null)
            }
          }
        })

        if (blob) {
          zip.file(`drawing-${drawing.id}-grayscale.png`, blob)
        }
      }

      // Generate zip
      const content = await zip.generateAsync({ type: 'blob' })
      saveAs(content, `drawings-grayscale-${new Date().getTime()}.zip`)

    } catch (e) {
      console.error("Batch download failed", e)
      alert("다운로드 중 오류가 발생했습니다.")
    } finally {
      setIsProcessingBatch(false)
      setBatchCurrentDrawing(null)
      setBatchCurrentPdfUrl(null)
      batchResolveRef.current = null
    }
  }

  // Callback when hidden canvas is ready
  const onBatchCanvasReady = () => {
    if (batchResolveRef.current) {
      // Add a small delay to ensure rendering is visually complete
      setTimeout(() => {
        batchResolveRef.current?.(true)
      }, 500)
    }
  }


  // No client-side filtering needed
  const paginatedDrawings = drawings

  const isAllPageSelected = paginatedDrawings.length > 0 && paginatedDrawings.every(d => selectedDrawingIds.has(d.id))

  if (selectedDrawingId !== null) {
    return <DrawingDetail drawingId={selectedDrawingId} onBack={handleBackToList} />
  }

  if (loading) {
    return (
      <div className="relative flex min-h-screen w-full bg-background-light font-display">
        <Sidebar />
        <main className="ml-64 flex flex-1 items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="material-symbols-outlined animate-spin">progress_activity</span>
            <span className="text-lg">로딩 중...</span>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen w-full bg-background-light font-display">
      <Sidebar />
      <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
        <div className="w-full max-w-7xl mx-auto">
          <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">문제 풀이</h1>
          </header>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Filter Box - Contains all filters */}
          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <div className="space-y-3">
              {/* Row 1: Search and Status Filters */}
              <div className="flex flex-wrap gap-3">
                <div className="flex w-full flex-1 items-stretch rounded-lg h-10 bg-white border border-gray-200 focus-within:ring-2 focus-within:ring-primary lg:w-auto lg:flex-1">
                  <div className="text-gray-400 flex items-center justify-center pl-3">
                    <span className="material-symbols-outlined">search</span>
                  </div>
                  <input
                    className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-lg text-gray-900 focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-gray-400 px-4 pl-2 text-sm"
                    placeholder="문제 이름으로 검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Status filters */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">상태:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setStatusFilter('default')}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${statusFilter === 'default'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      기본
                    </button>
                    <button
                      onClick={() => setStatusFilter('finished')}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${statusFilter === 'finished'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      완료
                    </button>
                    <button
                      onClick={() => setStatusFilter('live')}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${statusFilter === 'live'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      라이브
                    </button>
                    <button
                      onClick={() => setStatusFilter('all')}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${statusFilter === 'all'
                        ? 'bg-gray-800 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      모두
                    </button>
                  </div>
                </div>
              </div>

              {/* Row 2: User and Tag Filters */}
              <div className="flex flex-wrap gap-4">
                {/* User filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">사용자:</label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="all">전체</option>
                    {availableUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {[user.last_name, user.first_name].filter(Boolean).join(' ') || user.email}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Session filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">세션:</label>
                  <select
                    value={selectedSessionId}
                    onChange={(e) => setSelectedSessionId(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="all">전체</option>
                    {availableSessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tag filter */}
                <div className="flex-1 flex flex-wrap items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">태그:</label>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map((tag) => {
                      const isSelected = selectedTagIds.includes(tag.id)
                      return (
                        <button
                          key={tag.id}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id))
                            } else {
                              setSelectedTagIds([...selectedTagIds, tag.id])
                            }
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${isSelected
                            ? 'text-white ring-2 ring-offset-1'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          style={isSelected ? { backgroundColor: tag.color } : undefined}
                        >
                          {isSelected && (
                            <span className="material-symbols-outlined text-sm">check</span>
                          )}
                          <span>{tag.name}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Batch Actions */}
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleBatchDownload}
                disabled={selectedDrawingIds.size === 0 || isProcessingBatch}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessingBatch ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                    <span>처리 중 ({batchProgress.current}/{batchProgress.total})</span>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-base">download</span>
                    <span>선택 항목 흑백 PNG 다운로드 ({selectedDrawingIds.size})</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            {drawings.length === 0 ? (
              <div className="text-center py-12">
                <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">inbox</span>
                <p className="text-gray-500">
                  {searchQuery ? '검색 결과가 없습니다.' : '등록된 문제 풀이가 없습니다.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 w-10">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                            checked={isAllPageSelected}
                            onChange={toggleAllSelection}
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        id
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        문제
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        사용자
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        시간
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        태그
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        등록일시
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        상태
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        작업
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedDrawings.map((drawing) => {
                      const isSelected = selectedDrawingIds.has(drawing.id)
                      const durationMinutes = drawing.duration ? Math.floor(drawing.duration / 60) : 0
                      const durationSeconds = drawing.duration ? drawing.duration % 60 : 0
                      const isLongDuration = (drawing.duration || 0) >= 180

                      return (
                        <tr key={drawing.id} className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-center">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                                checked={isSelected}
                                onChange={() => toggleSelection(drawing.id)}
                              />
                            </div>
                          </td>
                          <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                            {drawing.id}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-sm font-medium text-gray-800">
                            {drawing.question?.name || '-'}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                            {drawing.user ? [drawing.user.last_name, drawing.user.first_name].filter(Boolean).join(' ') || '-' : '-'}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-sm">
                            {drawing.duration ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isLongDuration ? 'bg-yellow-100 text-yellow-800' : 'text-gray-500'}`}>
                                {durationMinutes}분 {durationSeconds}초
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="h-[72px] px-4 py-2">
                            {/* Tags display */}
                            {drawing.tags && drawing.tags.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {drawing.tags.map((tag) => (
                                  <span
                                    key={tag.id}
                                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full text-white"
                                    style={{ backgroundColor: tag.color }}
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                            {new Date(drawing.created_at).toLocaleString('ko-KR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="h-[72px] px-4 py-2">
                            {drawing.status === 400 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                <span className="material-symbols-outlined text-sm">check_circle</span>
                                <span>완료</span>
                              </span>
                            ) : drawing.status === 200 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                <span className="material-symbols-outlined text-sm">sensors</span>
                                <span>라이브</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-full">
                                <span className="material-symbols-outlined text-sm">play_circle</span>
                                <span>시작됨</span>
                              </span>
                            )}
                          </td>
                          <td className="h-[72px] px-4 py-2">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setSelectedDrawingId(drawing.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                              >
                                <span className="material-symbols-outlined text-sm">visibility</span>
                                <span>보기</span>
                              </button>
                              {canDeleteDrawing() && (
                                <button
                                  onClick={() => handleDelete(drawing.id)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                >
                                  <span className="material-symbols-outlined text-sm">delete</span>
                                  <span>삭제</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalFilteredCount > 0 && (
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Results summary */}
              <div className="text-sm text-gray-600">
                전체 {totalFilteredCount}개 중 {Math.min((currentPage - 1) * itemsPerPage + 1, totalFilteredCount)}-
                {Math.min(currentPage * itemsPerPage, totalFilteredCount)}개 표시
              </div>

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                {/* Items per page selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">페이지당:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value))
                      setCurrentPage(1) // Reset to first page when changing items per page
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value={10}>10개</option>
                    <option value={20}>20개</option>
                    <option value={50}>50개</option>
                    <option value={100}>100개</option>
                  </select>
                </div>

                {/* Page navigation */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                  >
                    <span className="material-symbols-outlined text-sm">chevron_left</span>
                    <span>이전</span>
                  </button>

                  <div className="flex items-center gap-1">
                    {(() => {
                      const totalPages = Math.ceil(totalFilteredCount / itemsPerPage)
                      const pages: (number | string)[] = []

                      if (totalPages <= 7) {
                        // Show all pages if 7 or fewer
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i)
                        }
                      } else {
                        // Show first page, last page, current page and neighbors
                        if (currentPage <= 3) {
                          // Near start
                          pages.push(1, 2, 3, 4, '...', totalPages)
                        } else if (currentPage >= totalPages - 2) {
                          // Near end
                          pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
                        } else {
                          // Middle
                          pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages)
                        }
                      }

                      return pages.map((page, idx) => (
                        page === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">...</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page as number)}
                            className={`min-w-[32px] rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${currentPage === page
                              ? 'bg-primary text-white'
                              : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        )
                      ))
                    })()}
                  </div>

                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(totalFilteredCount / itemsPerPage), currentPage + 1))}
                    disabled={currentPage >= Math.ceil(totalFilteredCount / itemsPerPage)}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                  >
                    <span>다음</span>
                    <span className="material-symbols-outlined text-sm">chevron_right</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main >

      {/* Hidden container for batch processing */}
      {isProcessingBatch && batchCurrentDrawing && (
        <div
          style={{
            position: 'fixed',
            top: '-9999px',
            left: '-9999px',
            width: '1200px', // Set a fixed width for consistent rendering
            height: 'auto',
            opacity: 0,
            pointerEvents: 'none'
          }}
          ref={canvasRef}
        >
          <DrawingCanvas
            drawingData={batchCurrentDrawing.drawing_data}
            pdfUrl={batchCurrentPdfUrl}
            isGrayscaleMode={true}
            setIsGrayscaleMode={() => { }} // No-op
            onCanvasReady={onBatchCanvasReady}
          />
        </div>
      )}
    </div >
  )
}
